import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';

import {
  apiGetGroupedBinderStatus,
  apiCreateBinderPDF,
  apiCreateESigning,
  apiResetSigning,
  apiUploadWetSignedPDF,
  apiCancelESigning,
  apiSendESigningReminder,
} from 'api';

import type {
  APIBinderPDF,
  APISigning,
  APIGroupedBinderStatusResponse,
  APIGroupedSigner,
  APIGroupedMissingEmail,
} from 'types/api.types';

import {
  BINDER_STATUS_GENERATING,
  BINDER_STATUS_COMPLETED,
  BINDER_STATUS_ERROR,
  SIGNING_STATUS_PENDING,
  SIGNING_STATUS_COMPLETED,
  STATUS_PENDING_CANCELLATION,
} from 'variables/constants';

interface UseGroupedBinderReturn {
  groupedStatus: APIGroupedBinderStatusResponse | null;
  binderPdf: APIBinderPDF | null;
  isGenerating: boolean;
  isCompleted: boolean;
  isError: boolean;
  fileUrl: string | null;
  signing: APISigning | null;
  isSigningPending: boolean;
  isSigningCompleted: boolean;
  signers: APIGroupedSigner[];
  missingEmails: APIGroupedMissingEmail[];
  generateBinder: () => void;
  createESigning: () => void;
  uploadWetSigned: (file: File) => void;
  resetSigning: () => void;
  cancelSigning: () => void;
  sendReminder: () => void;
  isGeneratePending: boolean;
  isSigningMutationPending: boolean;
  isUploadPending: boolean;
  isResetPending: boolean;
  isCancelPending: boolean;
  isReminderPending: boolean;
  isPolling: boolean;
}

export const useGroupedBinder = (
  uuid: string | undefined,
  policyGroupId: string | null | undefined,
): UseGroupedBinderReturn => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: groupedStatus, isFetching: isPolling } =
    useQuery<APIGroupedBinderStatusResponse>({
      queryKey: ['groupedBinderStatus', policyGroupId],
      queryFn: () => apiGetGroupedBinderStatus(uuid!),
      enabled: !!uuid && !!policyGroupId,
      refetchInterval: (query) => {
        const data = query.state.data;
        if (!data) return false;

        if (data.generation_status === 'generating') {
          return 5000;
        }

        if (
          data.signing &&
          (data.signing.status === SIGNING_STATUS_PENDING ||
            data.signing.status === STATUS_PENDING_CANCELLATION)
        ) {
          return 5000;
        }

        return false;
      },
    });

  const getBinderPdf = (): APIBinderPDF | null => {
    if (!groupedStatus) return null;

    const generationStatus = groupedStatus.generation_status;
    if (generationStatus === 'idle') return null;

    const policyWithBinder = groupedStatus.policies.find(
      (p) => p.binder_id !== null,
    );

    const statusMap: Record<string, number> = {
      generating: BINDER_STATUS_GENERATING,
      completed: BINDER_STATUS_COMPLETED,
      error: BINDER_STATUS_ERROR,
    };

    return {
      id: policyWithBinder?.binder_id || 0,
      status: statusMap[generationStatus],
      status_display:
        generationStatus.charAt(0).toUpperCase() + generationStatus.slice(1),
      file: groupedStatus.merged_file_url,
      updated_at: '',
      created_at: '',
    };
  };

  const binderPdf = getBinderPdf();

  const getSigning = (): APISigning | null => {
    if (!groupedStatus?.signing) return null;

    return {
      id: groupedStatus.signing.id,
      status: groupedStatus.signing.status,
      status_display: groupedStatus.signing.status_display,
      type: groupedStatus.signing.type,
      type_display:
        groupedStatus.signing.type === 1 ? 'E-Signing' : 'WET Signing',
      logs: groupedStatus.signing.logs || [],
      created_at: '',
      wet_signed_document: null,
    };
  };

  const signing = getSigning();

  const generateBinderMutation = useMutation({
    mutationFn: () => {
      if (!uuid) throw new Error('UUID is required');
      return apiCreateBinderPDF(uuid);
    },
    onSuccess: () => {
      toast({
        title: 'Binder generation started',
        description: 'Generating merged binder for all linked policies...',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      queryClient.invalidateQueries({
        queryKey: ['groupedBinderStatus', policyGroupId],
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error generating binder',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const createSigningMutation = useMutation({
    mutationFn: () => {
      if (!uuid || !binderPdf?.id) {
        throw new Error('UUID and Binder ID are required for signing');
      }
      return apiCreateESigning(uuid, binderPdf.id);
    },
    onSuccess: () => {
      toast({
        title: 'Signing initiated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      queryClient.invalidateQueries({
        queryKey: ['groupedBinderStatus', policyGroupId],
      });
      queryClient.invalidateQueries({ queryKey: ['submission'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error initiating signing',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const uploadWetSignedMutation = useMutation({
    mutationFn: (file: File) => {
      if (!uuid || !binderPdf?.id) {
        throw new Error('UUID and Binder ID are required');
      }
      const formData = new FormData();
      formData.append('file', file);
      return apiUploadWetSignedPDF(uuid, binderPdf.id, formData);
    },
    onSuccess: () => {
      toast({
        title: 'WET signed document uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      queryClient.invalidateQueries({
        queryKey: ['groupedBinderStatus', policyGroupId],
      });
      queryClient.invalidateQueries({ queryKey: ['submission'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error uploading WET signed document',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const resetSigningMutation = useMutation({
    mutationFn: () => {
      if (!uuid) throw new Error('UUID is required');
      return apiResetSigning(uuid);
    },
    onSuccess: () => {
      toast({
        title: 'Signing reset successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      queryClient.invalidateQueries({
        queryKey: ['groupedBinderStatus', policyGroupId],
      });
      queryClient.invalidateQueries({ queryKey: ['submission'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error resetting signing',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const cancelSigningMutation = useMutation({
    mutationFn: () => {
      if (!uuid || !signing?.id) {
        throw new Error('UUID and Signing ID are required');
      }
      return apiCancelESigning(uuid, signing.id);
    },
    onSuccess: () => {
      toast({
        title: 'Signing cancellation initiated',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      queryClient.invalidateQueries({
        queryKey: ['groupedBinderStatus', policyGroupId],
      });
      queryClient.invalidateQueries({ queryKey: ['submission'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error canceling signing',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const sendReminderMutation = useMutation({
    mutationFn: () => {
      if (!uuid || !signing?.id) {
        throw new Error('UUID and Signing ID are required');
      }
      return apiSendESigningReminder(uuid, signing.id);
    },
    onSuccess: () => {
      toast({
        title: 'Reminder sent',
        description:
          'A signing reminder is being sent to signers who have not yet signed.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Could not send reminder',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const isGenerating =
    groupedStatus?.generation_status === 'generating' ||
    generateBinderMutation.isPending;
  const isCompleted = groupedStatus?.generation_status === 'completed';
  const isError = groupedStatus?.generation_status === 'error';
  const fileUrl = binderPdf?.file || null;

  const isSigningPending = signing?.status === SIGNING_STATUS_PENDING;
  const isSigningCompleted = signing?.status === SIGNING_STATUS_COMPLETED;

  return {
    groupedStatus: groupedStatus || null,
    binderPdf,
    isGenerating,
    isCompleted,
    isError,
    fileUrl,
    signing,
    isSigningPending,
    isSigningCompleted,
    signers: groupedStatus?.signers || [],
    missingEmails: groupedStatus?.missing_emails || [],
    generateBinder: () => generateBinderMutation.mutate(),
    createESigning: () => createSigningMutation.mutate(),
    uploadWetSigned: (file: File) => uploadWetSignedMutation.mutate(file),
    resetSigning: () => resetSigningMutation.mutate(),
    cancelSigning: () => cancelSigningMutation.mutate(),
    sendReminder: () => sendReminderMutation.mutate(),
    isGeneratePending: generateBinderMutation.isPending,
    isSigningMutationPending: createSigningMutation.isPending,
    isUploadPending: uploadWetSignedMutation.isPending,
    isResetPending: resetSigningMutation.isPending,
    isCancelPending: cancelSigningMutation.isPending,
    isReminderPending: sendReminderMutation.isPending,
    isPolling,
  };
};
